/**
 * KpiCard — single KPI tile used at the top of dashboard / transactions
 * / orders pages.
 *
 * Phase-1 of the design refresh: replaces the 5-tile rainbow of
 * full-saturation gradients (blue+green+orange+green+red all on one
 * row) that overwhelmed customers on the Transactions page.
 *
 * The new variant is a white card with a thin coloured accent dot
 * (left-side) — the eye sees ONE coloured pixel per card and the
 * value itself becomes the dominant visual element, which is what
 * actually matters.
 *
 * Usage:
 *   <KpiCard
 *     label="Total Paid"
 *     value="€ 1 240.00"
 *     icon={<DollarSign className="w-3.5 h-3.5" />}
 *     tone="success"
 *     hint="Across 12 invoices"
 *   />
 */
import React from 'react';

const ACCENT = {
  primary: 'bg-cyan-500',
  success: 'bg-emerald-500',
  warn:    'bg-amber-500',
  danger:  'bg-rose-500',
  neutral: 'bg-slate-400',
};

export default function KpiCard({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  loading = false,
  testid,
  className = '',
}) {
  return (
    <div
      className={`relative bg-white border border-slate-200 rounded-xl px-4 py-3.5 flex flex-col gap-1 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-slate-300 transition-colors ${className}`}
      data-testid={testid}
    >
      {/* Coloured accent dot — single point of brand colour per card. */}
      <span
        className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${ACCENT[tone] || ACCENT.neutral}`}
        aria-hidden
      />
      <div className="pl-2 flex items-center gap-1.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">
        {icon && <span className="text-slate-400">{icon}</span>}
        {label}
      </div>
      <div className="pl-2 text-[22px] font-semibold text-slate-900 tabular-nums leading-tight">
        {loading ? <span className="inline-block w-16 h-5 bg-slate-100 rounded animate-pulse" /> : value}
      </div>
      {hint && (
        <div className="pl-2 text-[11px] text-slate-400 leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}
